import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";

import { useEffect, useMemo } from "react";

import { twMerge } from "tailwind-merge";

import CustomCard from "components/CustomCard";
import { entityUrl } from "components/EntityPageLayout";
import { Tab, Tabs } from "components/Tabs";
import { useApi } from "hooks/useApi";
import useMediaQuery from "hooks/useMediaQuery";
import shortSecurity from "utils/shortSecurity";

import EntityPageInsightsList from "./EntityPageInsightsList";
import styles from "./style.module.scss";

type EntityPageAnalysisProps = {};

const EntityPageAnalysis = ({}: EntityPageAnalysisProps) => {
  const router = useRouter();
  const { mobile } = useMediaQuery();
  const slug = router.query.slug as string;
  const currentTab = router.query.tab;

  const { data: entity } = useApi(entityUrl(slug));
  const { prettyName: name, security } = entity || {};
  const shortedSecurity = shortSecurity(security);
  const pageTitleName = useMemo(() => {
    if (shortedSecurity) {
      return `${name} | ${shortedSecurity} `;
    }
    return name;
  }, [shortedSecurity, name]);
  const title = `${pageTitleName} - Research, Discussions, Announcements, Analytics, SmartScore & News`;

  const shouldHidePrimer = useMemo(() => {
    const prettyName = entity?.prettyName?.toLowerCase();
    const security = entity?.security?.toLowerCase();

    return (
      /(msci|ftse)/.test(prettyName) || /(govt|comdty|curncy)/.test(security)
    );
  }, [entity?.prettyName, entity?.security]);

  useEffect(() => {
    if (currentTab === "primer" && shouldHidePrimer) {
      const params = router.query;
      const pathname = router.pathname;
      const newParams = new URLSearchParams(params as any);
      newParams.delete("tab");
      router.replace({ pathname, query: newParams.toString() }, undefined, {
        shallow: true,
      });
    }
  }, [currentTab, entity?.prettyName, shouldHidePrimer, router]);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta property="og:title" content={title} />
      </Head>
      <Tabs className="pt-0 md:!overflow-x-auto">
        <Tab
          href={`/entities/${slug}/research`}
          active={!currentTab}
          className="!flex !overflow-hidden items-center justify-center !min-w-[40px]"
        >
          All
        </Tab>
        <Tab
          href={`/entities/${slug}/research?tab=insights`}
          active={currentTab === "insights"}
          tooltip="Independent research published by verified providers"
        >
          Insights
        </Tab>
        {!shouldHidePrimer && (
          <Tab
            href={`/entities/${slug}/research?tab=primer`}
            active={currentTab === "primer"}
            tooltip="View or generate a company primer with Smartkarma AI"
            className="!flex !overflow-hidden !max-w-none !min-w-[110px]"
          >
            <div className="flex flex-1 items-center gap-[5px]">
              <Image
                src={`https://assets.smartkarma.com/icons/${
                  currentTab === "primer" ? "sparkle-white.svg" : "sparkle.svg"
                }`}
                alt="Sparkle"
                width={15}
                height={mobile ? 14 : 15}
              />
              Primers
            </div>
            <span
              className={twMerge(
                "bg-ink-pink px-[16px] py-[4px] text-[8px] ml-[5px] text-white absolute top-[2px] right-[-14px] rotate-45"
              )}
            >
              New
            </span>
          </Tab>
        )}
        <Tab
          href={`/entities/${slug}/research?tab=broker-reports`}
          active={currentTab === "broker-reports"}
          tooltip="Research published by sell-side firms"
          className="!min-w-[80px]"
        >
          Broker
        </Tab>
        <Tab
          href={`/entities/${slug}/research?tab=sponsored`}
          active={currentTab === "sponsored"}
          tooltip="Research sponsored by issuers/companies or public/private market exchanges"
          className="!flex !overflow-hidden !max-w-none !min-w-[100px]"
        >
          Sponsored
        </Tab>
        <Tab
          href={`/entities/${slug}/research?tab=syndicated`}
          active={currentTab === "syndicated"}
          tooltip="Research curated from across the web"
          className="!flex !overflow-hidden !max-w-none !min-w-[100px]"
        >
          Syndicated
        </Tab>
        <Tab
          href={`/entities/${slug}/research?tab=podcast`}
          active={currentTab === "podcast"}
          tooltip="Audio curated from across the web"
        >
          Podcast
        </Tab>
        <Tab
          href={`/entities/${slug}/research?tab=buyside`}
          active={currentTab === "buyside"}
          tooltip="Research/letters published by the buy-side"
        >
          Buy side
        </Tab>
      </Tabs>
      <CustomCard
        className={twMerge(
          styles.card,
          "mb-0 md:mb-[30px]",
          "min-h-[max(calc(100vh-500px),720px)]"
        )}
      >
        <EntityPageInsightsList />
      </CustomCard>
    </>
  );
};

export default EntityPageAnalysis;
